import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { X, AlertCircle, RotateCcw, Check } from 'lucide-react';
import { formatFileSize, getFileIcon, isImage, createImagePreview } from '@/utils/fileHelpers';
import type { FileUploadProgress, UploadedFile } from '@/hooks/useDirectUpload';

interface FilePreviewProps {
  files: File[];
  uploadProgress: Map<string, FileUploadProgress>;
  uploadedFiles?: UploadedFile[];
  onRemove: (index: number) => void;
  onRetry: (fileKey: string) => Promise<void>;
  getFileKey: (file: File) => string;
}

interface FileWithPreview {
  file: File;
  preview?: string;
}

// Circular progress overlay component
function CircularProgress({ progress, isCompleted }: { progress: number; isCompleted: boolean }) {
  const [showCheck, setShowCheck] = useState(false);
  const [hideOverlay, setHideOverlay] = useState(false);
  const size = 50;
  const strokeWidth = 2.5;
  const radius = (size - strokeWidth) / 2;
  const circumference = radius * 2 * Math.PI;
  const offset = circumference - (progress / 100) * circumference;

  useEffect(() => {
    if (isCompleted) {
      setShowCheck(true);
      const timer = setTimeout(() => {
        setShowCheck(false);
        setHideOverlay(true);
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [isCompleted]);

  if (progress === 0 || hideOverlay) return null;

  return (
    <div className="absolute inset-0 flex items-center justify-center bg-blue-500/15 rounded-lg backdrop-blur-sm">
      <svg width={size} height={size} className="transform -rotate-90">
        {/* Background circle */}
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          stroke="rgba(255, 255, 255, 0.2)"
          strokeWidth={strokeWidth}
          fill="none"
        />
        {/* Progress circle */}
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          stroke="white"
          strokeWidth={strokeWidth}
          fill="none"
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          strokeLinecap="round"
          className="transition-all duration-300 ease-out"
        />
      </svg>

      {/* Checkmark overlay */}
      {showCheck && (
        <div className="absolute inset-0 flex items-center justify-center animate-in fade-in zoom-in duration-300">
          <div className="w-6 h-6 bg-green-500 rounded-full flex items-center justify-center">
            <Check className="w-4 h-4 text-white" strokeWidth={2.5} />
          </div>
        </div>
      )}

      {/* Progress percentage */}
      {!showCheck && !isCompleted && (
        <div className="absolute inset-0 flex items-center justify-center">
          <span className="text-white text-xs font-semibold">{progress}%</span>
        </div>
      )}
    </div>
  );
}

export function FilePreview({ files, uploadProgress, uploadedFiles, onRemove, onRetry, getFileKey }: FilePreviewProps) {
  const { t } = useTranslation('conversations');
  const [filesWithPreviews, setFilesWithPreviews] = useState<FileWithPreview[]>([]);
  // Track which previews have been swapped to Rails URLs
  const [swappedToRails, setSwappedToRails] = useState<Set<string>>(new Set());

  // Helper to find uploaded file data by filename (for pre-uploaded assets)
  const findUploadedFile = (file: File): UploadedFile | undefined => {
    return uploadedFiles?.find(uf => uf.filename === file.name);
  };

  useEffect(() => {
    const loadPreviews = async () => {
      const previews = await Promise.all(
        files.map(async (file) => {
          // Check if this is a pre-uploaded asset with existing preview
          const uploadedFile = findUploadedFile(file);
          if (uploadedFile?.blobUrl && file.size === 0) {
            // Use the pre-existing preview URL for assets from picker
            return { file, preview: uploadedFile.blobUrl };
          }

          if (isImage(file.type)) {
            try {
              const preview = await createImagePreview(file);
              return { file, preview };
            } catch (error) {
              console.error('Failed to create preview:', error);
              return { file };
            }
          }
          return { file };
        })
      );
      setFilesWithPreviews(previews);
    };

    loadPreviews();
  }, [files, uploadedFiles]);

  // When upload completes, swap blob URL to small variant URL in the preview
  useEffect(() => {
    if (!uploadProgress) return;

    uploadProgress.forEach((progress, fileKey) => {
      if (progress.status === 'completed' && progress.url && !swappedToRails.has(fileKey)) {
        // Find the file with preview
        const fileWithPreview = filesWithPreviews.find(f => getFileKey(f.file) === fileKey);
        if (fileWithPreview?.preview && isImage(fileWithPreview.file.type)) {
          // Use small variant URL if available (~40KB), otherwise original
          const displayUrl = progress.smallUrl || progress.url;

          // Preload Rails URL, then swap
          const img = new Image();
          img.onload = () => {
            // Swap blob URL to small variant in preview
            setFilesWithPreviews(prev => prev.map(item => {
              if (getFileKey(item.file) === fileKey) {
                // Revoke old blob URL
                if (item.preview) {
                  URL.revokeObjectURL(item.preview);
                }
                return { ...item, preview: displayUrl };
              }
              return item;
            }));
            setSwappedToRails(prev => new Set([...prev, fileKey]));
          };
          img.onerror = () => {
            // Preload failed, keep blob URL
            console.warn('Failed to preload small variant URL for preview');
          };
          img.src = displayUrl;
        }
      }
    });
  }, [uploadProgress, filesWithPreviews, swappedToRails, getFileKey]);

  if (files.length === 0) return null;

  return (
    <div className="flex flex-wrap gap-3 p-3 bg-primary/5 rounded-xl border border-primary/10">
      {filesWithPreviews.map((item, index) => {
        const fileKey = getFileKey(item.file);
        const progress = uploadProgress?.get(fileKey);
        const isUploading = progress?.status === 'uploading';
        const hasError = progress?.status === 'error';
        const isCompleted = progress?.status === 'completed';
        const uploadPercentage = progress?.progress || 0;
        // Get the correct file size (use uploadedFile.byteSize for pre-uploaded assets)
        const uploadedFile = findUploadedFile(item.file);
        const displaySize = item.file.size > 0 ? item.file.size : (uploadedFile?.byteSize || 0);

        return (
          <div
            key={fileKey}
            className="relative group flex flex-col gap-2"
          >
            {/* Remove button - always visible on hover */}
            <button
              onClick={() => onRemove(index)}
              className="absolute -top-1.5 -right-1.5 p-1 bg-destructive hover:bg-destructive/80 text-destructive-foreground rounded-full opacity-0 group-hover:opacity-100 transition-opacity z-20 shadow-md"
              title={t('chat.removeFile')}
              disabled={isUploading}
            >
              <X className="w-3.5 h-3.5" />
            </button>

            {/* Image/File preview with circular progress overlay */}
            <div className="relative w-20 h-20 flex-shrink-0">
              {item.preview ? (
                <img
                  src={item.preview}
                  alt={item.file.name}
                  className="w-full h-full object-cover rounded-lg"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center bg-muted rounded-lg text-3xl">
                  {getFileIcon(item.file.type)}
                </div>
              )}

              {/* Circular progress overlay */}
              {(isUploading || isCompleted) && (
                <CircularProgress progress={uploadPercentage} isCompleted={isCompleted} />
              )}

              {/* Error overlay */}
              {hasError && (
                <div className="absolute inset-0 flex flex-col items-center justify-center bg-destructive/90 rounded-lg">
                  <AlertCircle className="w-6 h-6 text-white mb-1" />
                  <button
                    onClick={() => onRetry(fileKey)}
                    className="flex items-center gap-1 text-xs text-white hover:text-white/80 transition-colors"
                  >
                    <RotateCcw className="w-3 h-3" />
                    {t('chat.retry')}
                  </button>
                </div>
              )}
            </div>

            {/* File info below preview */}
            <div className="flex flex-col w-20 min-w-0">
              <span className="text-xs font-medium truncate" title={item.file.name}>
                {item.file.name}
              </span>
              <span className="text-[10px] text-muted-foreground">
                {formatFileSize(displaySize)}
              </span>
            </div>
          </div>
        );
      })}
    </div>
  );
}
